import { Middleware, isAction, PayloadAction } from '@reduxjs/toolkit';
import { setDynamicSegment, clearDynamicSegment } from '../slices/organizationsSlice';

// Constants for localStorage validation
const MAX_STORAGE_SIZE = 5 * 1024; // 5KB limit for our data
const MAX_STRING_LENGTH = 1000; // Maximum length for individual string values
const ALLOWED_KEY_PATTERN = /^[a-zA-Z0-9_-]+$/; // Only allow safe characters in keys

// Whitelist of safe-to-persist state keys
const SAFE_PERSIST_KEYS = new Set([
  'orgadmin_dynamic_segment',
  'orgadmin_theme_preference',
  'orgadmin_last_visited',
  'orgadmin_user_preferences'
]);

/**
 * Validates data before storing in localStorage
 */
const validateForStorage = (key: string, value: string): { isValid: boolean; error?: string } => {
  // Check if key is in whitelist
  if (!SAFE_PERSIST_KEYS.has(key)) {
    return { isValid: false, error: 'Key not in safe persist whitelist' };
  }

  // Validate key format
  if (!ALLOWED_KEY_PATTERN.test(key)) {
    return { isValid: false, error: 'Invalid key format' };
  }

  // Check if value is a string and not empty
  if (typeof value !== 'string' || value.length === 0) {
    return { isValid: false, error: 'Value must be a non-empty string' };
  }

  // Check string length
  if (value.length > MAX_STRING_LENGTH) {
    return { isValid: false, error: `Value exceeds maximum length of ${MAX_STRING_LENGTH} characters` };
  }

  return { isValid: true };
};

/**
 * Safely stores data in localStorage with validation and error handling
 */
const safeLocalStorageSet = (key: string, value: string): boolean => {
  try {
    const validation = validateForStorage(key, value);

    if (!validation.isValid) {
      console.warn(`localStorage validation failed for key "${key}": ${validation.error}`);
      return false;
    }

    // Check size of our own keys only (not OIDC tokens etc. stored by other libraries)
    const ownSize = Array.from(SAFE_PERSIST_KEYS)
      .map(k => (localStorage.getItem(k) ?? '').length + k.length)
      .reduce((a, b) => a + b, 0);
    if (ownSize + key.length + value.length > MAX_STORAGE_SIZE) {
      console.warn(`localStorage size limit would be exceeded. Current: ${ownSize}, Adding: ${key.length + value.length}`);
      return false;
    }

    // Store the data
    localStorage.setItem(key, value);
    return true;
  } catch (error) {
    console.error('Failed to store in localStorage:', error);
    return false;
  }
};

/**
 * Safely retrieves data from localStorage with validation
 */
const safeLocalStorageGet = (key: string): string | null => {
  try {
    if (!ALLOWED_KEY_PATTERN.test(key)) {
      console.warn(`Invalid key format for retrieval: "${key}"`);
      return null;
    }

    return localStorage.getItem(key);
  } catch (error) {
    console.error('Failed to retrieve from localStorage:', error);
    return null;
  }
};

/**
 * Safely removes data from localStorage with error handling
 */
const safeLocalStorageRemove = (key: string): boolean => {
  try {
    if (!ALLOWED_KEY_PATTERN.test(key)) {
      console.warn(`Invalid key format for removal: "${key}"`);
      return false;
    }

    localStorage.removeItem(key);
    return true;
  } catch (error) {
    console.error('Failed to remove from localStorage:', error);
    return false;
  }
};

/**
 * Middleware to handle localStorage side effects for Redux actions
 * This keeps reducers pure by moving side effects to middleware
 */
export const localStorageMiddleware: Middleware = (storeAPI) => (next) => (action: unknown) => {
  // Call the next dispatch method in the middleware chain (reducer runs here)
  const result = next(action);

  // Only process valid Redux actions
  if (!isAction(action)) {
    return result;
  }

  // Handle localStorage side effects after the action has been processed
  switch (action.type) {
    case setDynamicSegment.type: {
      const payloadAction = action as PayloadAction<string>;
      if (typeof payloadAction.payload === 'string') {
        safeLocalStorageSet('orgadmin_dynamic_segment', payloadAction.payload);
      }
      break;
    }
    case clearDynamicSegment.type:
      safeLocalStorageRemove('orgadmin_dynamic_segment');
      break;
    case 'organizations/fetchOrganizations/fulfilled': {
      // Read dynamicSegment from the NEW state (after the reducer ran).
      // The reducer may auto-select a different org when the stored segment no
      // longer exists in the returned list — we must persist that correction so
      // a hard-refresh at any route uses the right value instead of a stale one.
      const newSegment = (storeAPI.getState() as { organizations?: { dynamicSegment?: string } }).organizations?.dynamicSegment;
      if (newSegment && typeof newSegment === 'string') {
        safeLocalStorageSet('orgadmin_dynamic_segment', newSegment);
      }
      break;
    }
  }

  return result;
};

// Export safe localStorage functions for use in other parts of the app
export { safeLocalStorageGet, safeLocalStorageSet, safeLocalStorageRemove };
